import { AfterViewInit, Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild, } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EmptyError, Subject, first, takeUntil } from 'rxjs';
import { HostDirective } from '../../directives/host/host.directive';
import { ResourceEntry, ResourceService } from 'src/app/modules/shared/services/resource.service';
import { IframeEvent } from '../../models/iframe-event/iframe-event';
import { InstructionSearchService } from 'src/app/modules/shared/components/instruction-search/instruction-search.service';

@Component({
    selector: 'app-instruction-item',
    templateUrl: './instruction-item.component.html',
    styleUrls: ['./instruction-item.component.scss']
})
export class InstructionItemComponent implements AfterViewInit, OnInit, OnDestroy {

    @Input() public showCollapseControls: boolean;
    @Input() public label: string;
    @Input() public id: string;
    @Input() public thumbnail?: string;
    @Input() public collapsed: boolean;
    @Input() public print: boolean;
    @Input() public ShowSendToIwmButton: boolean = false;
    @Input() public parentLabel: string = '';

    @Input() @HostBinding('class.is-first') public isFirst: boolean;
    @Input() @HostBinding('class.is-last') public isLast: boolean;

    @Output() emitPrint: EventEmitter<string> = new EventEmitter();

    @ViewChild(HostDirective, { static: true }) public hostDirective: HostDirective;

    public thumbnailSource?: SafeResourceUrl;
    public thumbnailLoading: boolean = false;
    private destroy$: Subject<void> = new Subject();

    constructor(
        private route: ActivatedRoute,
        private sanitizer: DomSanitizer,
        private resourceService: ResourceService,
        private instructionSearchService: InstructionSearchService) { }

    ngOnInit() {
        this.route.fragment
            .pipe(
                first()
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(fragment => {
                this.collapsed = this.showCollapseControls && fragment !== this.id;
            });

        if (this.thumbnail && !this.print) {
            this.fetchThumbnail();
        }

        this.instructionSearchService.collapseAll$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.collapsed = false;
            });
    }

    ngAfterViewInit() {
        this.route.fragment
            .pipe(
                first()
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(fragment => {
                if (fragment === this.id) {
                    this.scrollToFragment(fragment, true);
                }
            });
    }

    public addSectionToIWM(e: any) {

        const message: IframeEvent = {
            type: 'instruction-item',
            from: 'child',
            msg: {
                id: this.id,
                name: this.parentLabel + ' | ' + this.label,
                link: `${location.origin}${location.pathname}#${this.id}`
            }
        };

        // Send the message to the parent using the postMessage
        window.parent.postMessage(message, "*");
        e.stopPropagation();
    }


    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public toggleCollapse() {
        this.collapsed = !this.collapsed;
    }

    public scrollToFragmentFromLink() {
        let url = `${location.origin}${location.pathname}#${this.id}`;
        this.resourceService.copyToClipboard(url, 'default.linkCopied');

        setTimeout(() => {
            this.scrollToFragment(this.id);
        }, 250);
    }

    public printItem() {
        this.emitPrint.emit(this.id);
    }

    private async fetchThumbnail() {
        if (this.resourceService.isPrivateResource(this.thumbnail)) {
            this.thumbnailLoading = true;

            let resource: ResourceEntry;

            try {
                resource = await this.resourceService.getResource('image', this.thumbnail);
            }
            catch (error) {
                // EmptyError is thrown if service disposes the resources while request is in progress.
                //
                // This happens when user navigates away from the instruction while downloading,
                // so just ignore the error and return.
                if (error instanceof EmptyError) {
                    return;
                }

                // Otherwise ignore error and continue execution
            }

            // Use dataUrl (base64) for iOS compatibility with authenticated requests.
            // Blob URLs can fail on iOS Safari when created from authenticated HTTP requests.
            // DataUrl is constructed only for ios devices. Other platforms use objectUrl for better memory efficiency.
            const url = resource?.dataUrl ?? resource?.objectUrl ?? '';

            this.thumbnailSource = this.sanitizer.bypassSecurityTrustUrl(url);

            this.thumbnailLoading = false;
        }
        // Public resource, no access token header needed
        else {
            const url = this.resourceService.buildResourceUrl('image', this.thumbnail);

            this.thumbnailSource = this.sanitizer.bypassSecurityTrustUrl(url);
        }
    }

    private async scrollToFragment(fragment: string, auto: boolean = false) {
        const el = document.getElementById(fragment);
        if (!el) return;

        // Wait for all images and videos in the document to load
        const media = Array.from(document.querySelectorAll('img, video')) as (HTMLImageElement | HTMLVideoElement)[];
        await Promise.all(media.map(m => {
            if (m instanceof HTMLImageElement) {
                return m.complete ? Promise.resolve() : new Promise(res => { m.addEventListener('load', res, { once: true }); m.addEventListener('error', res, { once: true }); });
            } else if (m instanceof HTMLVideoElement) {
                return m.readyState >= 2 ? Promise.resolve() : new Promise(res => { m.addEventListener('loadeddata', res, { once: true }); m.addEventListener('error', res, { once: true }); });
            }
            return Promise.resolve();
        }));

        // Helper to scroll and check position
        const scrollAndCheck = (attempt = 0) => {
            window.scroll({
                top: el.offsetTop - 32,
                behavior: auto ? 'auto' : 'smooth'
            });
            requestAnimationFrame(() => {
                // If not at the right position, try again (max 3 times)
                const y = window.scrollY;
                const targetY = el.offsetTop - 32;
                if (Math.abs(y - targetY) > 2 && attempt < 3) {
                    setTimeout(() => scrollAndCheck(attempt + 1), 100);
                }
            });
        };
        scrollAndCheck();
    }

}
